import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ambientFromImage, ambientHost } from '../../utils/ambientColor';
import { coverAt } from '../../utils/cover';
import { coverChain } from '../../utils/coverChain';

// The url to paint once it is actually loadable. The nav hands a page the
// row's picture (an older serving-table url) and the page response can
// carry a newer one (Apple after the 10-28 flip): swapping the <img src>
// on arrival flashed the header. The first picture stays painted until the
// new one has loaded; a dead url (404 avatar) moves down `covers`, and when
// nothing loads the last good picture stays.
// A url the browser already holds (nav → page with the same picture) paints
// on the first frame; anything else waits for its load, so a dead url is
// never painted, not even for a frame.
const cachedNow = (url, px) => {
    if (!url || typeof Image === 'undefined') return false;
    const img = new Image(); img.src = coverAt(url, px);
    return img.complete && img.naturalWidth > 0;
};
// `entityKey`: the previous picture is kept painted only while the ENTITY is
// the same (a newer url for the same record); another record's picture never
// lingers on this one's page (Erez 10-28: Joris Voorn's cover stayed on DJ
// HEARTSTRING's page when HEARTSTRING's own cover failed to load). When every
// candidate fails, nothing is shown.
function useSettledSrc(wanted, covers, px, entityKey = null) {
    const [shown, setShown] = useState(() => (cachedNow(wanted, px) ? wanted : null));
    // was the first picture already in the browser cache (e.g. painted by the page's loading header)? then it paints
    // without the fade-in, which read as a blink (Erez 10-28)
    const instant = useRef(shown !== null);
    const chainKey = coverChain(wanted, covers).join('\n');
    const lastKey = useRef(entityKey);
    useEffect(() => {
        if (lastKey.current !== entityKey) { lastKey.current = entityKey; setShown(cachedNow(wanted, px) ? wanted : null); }
        const chain = coverChain(wanted, covers);
        if (!chain.length) { setShown(null); return undefined; }
        let cancelled = false;
        const tryAt = (i) => {
            if (cancelled) return;
            if (i >= chain.length) { setShown(null); return; }
            const img = new Image();
            img.onload = () => { if (!cancelled) setShown(chain[i]); };
            img.onerror = () => tryAt(i + 1);
            img.src = coverAt(chain[i], px);
        };
        tryAt(0);
        return () => { cancelled = true; };
    }, [chainKey, px, entityKey]); // eslint-disable-line react-hooks/exhaustive-deps
    return [shown, instant.current && shown === wanted];
}

// DESIGN V2 — the hero header for entity pages (artist / track / mix / label)
// and the For You landing. Big art, big name, ambient light sampled from the
// artwork, ONE primary action (Play), the rest ghost.
//
//   kicker        "✓ Verified artist · Electronic"
//   title         "Surgeon"                       (56-64px, 800)
//   meta          [United Kingdom] · [Featured in 3,925 mixes] · [...]
//   metaSecond    a second, quieter meta row: the track page's facts under its
//                 artist byline ([Label] · 2021 · 125 BPM · Am), so a long credit
//                 list never pushes them off the line (Erez 11-01)
//   lines         "Member of: Anti Up"            (identity lines, above sub)
//   sub           "Supported by Joel Mull, Shlomi Aber and 1,129 others"
//   actions       [▶ Play] [+ Follow] [⇪] […]  + platform links
//   facts         4,460 DJ plays | 1,131 supporting DJs | ...   (quiet strip)
//
// `art` is an <img src> (or a React node), `round` makes it a circle (artists).
// `covers` = the entity's full picture list (fallbacks for `art`). `banner` =
// the entity's second photo, a DIFFERENT picture with air around the subject
// (the server decides: heroCover), painted as a cover band on the page behind
// the hero: the photo whole and sharp on the right, the same photo blown up
// and blurred as the room's light, fading into the page above the facts strip.
// `bannerFocus` = {cx, cy, s}: the face in the banner (centre and width as fractions of the picture). Given, the
// sharp photo shows a head-and-shoulders window around it, so the text a set cover carries above and below the
// DJ (title, date, label) stays out of the band (Erez 10-30, Chris Lake / Adam Beyer). A face already filling
// most of the picture gets the whole picture, as before.
export function bannerWindow(f) {
    if (!f || !(f.s > 0) || !(f.cx >= 0) || !(f.cy >= 0)) return null;
    const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
    const top = clamp(f.cy - 0.9 * f.s, 0, 1), bottom = clamp(f.cy + 1.25 * f.s, 0, 1);
    const wh = Math.max(0.35, bottom - top);
    if (wh >= 0.9) return null;
    const y = clamp(top, 0, 1 - wh), x = clamp(f.cx - wh / 2, 0, 1 - wh);
    const pct = (v) => `${(100 * v / (1 - wh)).toFixed(2)}%`;
    return { size: `auto ${(100 / wh).toFixed(2)}%`, pos: `${pct(x)} ${pct(y)}` };
}
export default function HeroHeader({
    art, artAlt = '', round = false, covers, banner = null, bannerFocus = null, kicker, title, meta = [], metaSecond = [], lines = null, sub, subIcon = null, actions, facts = [], ambientKey, ambientSrc, className = '', children,
}) {
    const rootRef = useRef(null);
    const [settledArt, artInstant] = useSettledSrc(typeof art === 'string' ? art : null, covers, 400);
    const [settledBanner] = useSettledSrc(banner, null, 800, ambientKey);
    const win = bannerWindow(bannerFocus);
    // The band lives INSIDE the scroll pane when there is one (`.lower-part`:
    // its gutters are folded into the pane, see _design-v2.scss), so it
    // scrolls natively on the compositor with the rest of the page. It used
    // to sit on the frame outside the pane and follow the scroll by hand
    // (transform on each scroll event): one frame behind the native scroll,
    // which read as sticky (Erez 10-28). The frame stays the fallback host
    // for a page without a scroll pane. Edge to edge, from the pane's top
    // down to the facts strip.
    const [band, setBand] = useState(null); // { host, height }
    useLayoutEffect(() => {
        if (!settledBanner) { setBand(null); return undefined; }
        const frame = ambientHost(rootRef.current); if (!frame) return undefined;
        let pane = rootRef.current?.parentElement;
        while (pane && pane !== frame && pane !== document.body && !/auto|scroll/.test(getComputedStyle(pane).overflowY)) pane = pane.parentElement;
        const scroller = pane && pane !== frame && pane !== document.body ? pane : null;
        const host = scroller || frame;
        const measure = () => {
            const facts = rootRef.current?.querySelector('.v2-hero-facts');
            const edge = (facts || rootRef.current)?.getBoundingClientRect(); const hr = host.getBoundingClientRect();
            if (!edge) return;
            const scrollTop = scroller ? scroller.scrollTop : 0;
            setBand({ host, height: Math.max(0, Math.round((facts ? edge.top : edge.bottom) - hr.top + scrollTop)) });
        };
        measure();
        const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
        ro?.observe(host); if (rootRef.current) ro?.observe(rootRef.current);
        return () => { ro?.disconnect(); };
    }, [settledBanner]);
    // `ambientSrc` lets a page pass the art as a node (e.g. wrapped in a
    // preview player) and still light the room from the image URL.
    const artSrc = typeof art === 'string' ? settledArt : (ambientSrc || null);
    // With a cover band the room is lit by the band (the page reads as one:
    // a blue photo, a blue room), else by the art (Erez 10-28: DJ HEARTSTRING's
    // light-blue band under a brown room sampled from the Apple avatar).
    const lightSrc = settledBanner || artSrc;

    // Light the room with the artwork. Runs once per art src; falls back to the
    // default warm glow when the image can't be sampled (CORS, missing).
    useEffect(() => {
        let cancelled = false;
        const el = ambientHost(rootRef.current);
        if (!el) return undefined;
        // A page that WANTS a picture keeps the previous room light until its own has loaded and been sampled: clearing
        // it on mount flipped the room to the default glow for the ~700 ms the picture took, then to the new colour
        // (the flash, Erez 10-28). Only a page without any picture lets the light go.
        if (!lightSrc) { if (!art && !banner) { el.style.removeProperty('--v2-ambient'); el.style.removeProperty('--v2-ambient-2'); } return undefined; }
        ambientFromImage(lightSrc).then((c) => {
            if (cancelled || !c) return;
            el.style.setProperty('--v2-ambient', c.primary);
            el.style.setProperty('--v2-ambient-2', c.secondary);
        });
        return () => { cancelled = true; };
    }, [lightSrc, ambientKey]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <header className={`v2-hero ${className}${settledBanner ? ' has-banner' : ''}`} ref={rootRef}>
            {band && band.height > 0 && createPortal(
                <div className="v2-hero-cover" style={{ height: band.height, '--v2-cover': `url("${coverAt(settledBanner, 800)}")`, ...(win && settledBanner === banner ? { '--v2-cover-size': win.size, '--v2-cover-pos': win.pos } : {}) }} aria-hidden="true">
                    <div className="v2-hero-cover-light" />
                    <div className="v2-hero-cover-photo" />
                    <div className="v2-hero-cover-fade" />
                </div>, band.host)}
            <div className={`v2-hero-art${round ? ' round' : ''}`}>
                {typeof art === 'string' ? (settledArt ? <img src={coverAt(settledArt, 400)} alt={artAlt} className={artInstant ? 'v2-art-instant' : undefined} /> : null) : art}
            </div>
            <div className="v2-hero-body">
                {kicker && <div className="v2-hero-kicker">{kicker}</div>}
                <h1 className="v2-hero-title">{title}</h1>
                {meta.length > 0 && (
                    <div className="v2-hero-meta">
                        {meta.filter(Boolean).map((m, i) => (
                            <React.Fragment key={i}>{i > 0 && <i className="v2-hero-sep" />}<span>{m}</span></React.Fragment>
                        ))}
                    </div>
                )}
                {metaSecond.filter(Boolean).length > 0 && (
                    <div className="v2-hero-meta v2-hero-meta-second">
                        {metaSecond.filter(Boolean).map((m, i) => (
                            <React.Fragment key={i}>{i > 0 && <i className="v2-hero-sep" />}<span>{m}</span></React.Fragment>
                        ))}
                    </div>
                )}
                {lines && <div className="v2-hero-lines">{lines}</div>}
                {sub && <div className="v2-hero-sub">{subIcon}{sub}</div>}
                {actions && <div className="v2-hero-actions">{actions}</div>}
                {children}
            </div>
            {facts.length > 0 && (
                <div className="v2-hero-facts">
                    {facts.map((f, i) => (
                        <div className="v2-fact" key={i} title={f.title}>
                            <b className="v2-num">{f.value}</b>
                            <span>{f.label}{f.delta && <em>{f.delta}</em>}</span>
                        </div>
                    ))}
                </div>
            )}
        </header>
    );
}
